import React, {
  FC, useCallback, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import type { AISummary as AISummaryType } from 'src/common/types/api/v4/boats/search';
import Icon from 'src/common/components/IconDS22';
import Typography from 'src/common/components/Typography';
import Button from 'src/common/components/ButtonDS22';
import { trackEvent } from 'src/common/3rdParty/tracking';
import styles from './AISummary.module.scss';

export type AISummaryProps = {
  aiSummary?: AISummaryType;
  hasSummary: boolean;
  listingId: string;
};

type Feedback = 'thumbs-up' | 'thumbs-down';

const AISummary: FC<AISummaryProps> = ({
  aiSummary,
  hasSummary,
  listingId,
}) => {
  const [t] = useTranslation('listing-page');
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);

  const trackFeedback = useCallback((
    feedback: Feedback,
  ) => {
    setHasSubmittedFeedback(true);
    trackEvent('listing_page_ai_summary_feedback', {
      p1: listingId,
      p2: feedback,
    });
  }, [listingId]);

  if (!aiSummary) return null;
  const tags = aiSummary?.great_for || [];

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        {/* Section 1: Three content sections with icons */}
        {hasSummary && (
          <div className={styles.summarySections}>
            {aiSummary?.experience_info && aiSummary.experience_info !== 'No user reviews for this listing.' && (
              <div className={styles.summarySection}>
                <Icon id="map-treasure" size="l" />
                <Typography type="span" size="body-l">
                  {aiSummary.experience_info}
                </Typography>
              </div>
            )}

            {aiSummary?.boat_info && (
              <div className={styles.summarySection}>
                <Icon id="category-yachts" size="l" />
                <Typography type="span" size="body-l">
                  {aiSummary.boat_info}
                </Typography>
              </div>
            )}

            {aiSummary?.owner_info && (
              <div className={styles.summarySection}>
                <Icon id="captain" size="l" />
                <Typography type="span" size="body-l">
                  {aiSummary.owner_info}
                </Typography>
              </div>
            )}
          </div>
        )}

        {/* Section 2: Tags with cyan chips */}
        {tags.length > 0 && (
          <div className={styles.tagsSection}>
            <Typography type="span" size="body-m">
              {t('ai-summary-tags-label')}
            </Typography>
            <div className={styles.tags}>
              {tags.map((tag) => (
                <span key={tag} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className={styles.feedbackAndAttributionSection}>
          {/* Section 3: Feedback section */}
          <div className={styles.feedbackSection}>
            {hasSubmittedFeedback
              ? (
                <Typography type="span" size="body-s">
                  {t('ai-summary-feedback-thank-you')}
                </Typography>
              ) : (
                <>
                  <Typography type="span" size="body-s">
                    {t('ai-summary-feedback-label')}
                  </Typography>
                  <Button
                    icon="thumbs-up"
                    size="small"
                    variant="ghost-dark"
                    onClick={() => trackFeedback('thumbs-up')}
                  />
                  <Button
                    icon="thumbs-down"
                    size="small"
                    variant="ghost-dark"
                    onClick={() => trackFeedback('thumbs-down')}
                  />
                </>
              )}
          </div>

          {/* Section 4: AI attribution */}
          <div className={styles.attributionSection}>
            <div className={styles.sectionIcon}>
              <Icon id="ai" size="s" />
            </div>
            <Typography type="span" size="body-s">
              {t('ai-summary-attribution')}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISummary;
